import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { MutationCache, QueryClientProvider as OriginalQueryClientProvider, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useMemo, type FC, type PropsWithChildren } from "react";
import { useDispatch } from "react-redux";
import { READING_MODE_ERROR, type ErrorWithType } from "~/shared/api/server";
import dispatchers from "~/shared/dispatchers";
import mbc from "~/shared/functions/mbc";
import { mbcStorage, serializeDeserialize } from "~/shared/mbc/mbcStorage";

declare global {
	interface Window {
		__TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
	}
}

export const persister = createAsyncStoragePersister({
	storage: mbcStorage,
	serialize: serializeDeserialize,
	deserialize: serializeDeserialize,
	key: "ppm-react-query",
});

const QueryClientProvider: FC<PropsWithChildren> = ({ children }) => {
	const dispatch = useDispatch();
	const queryClient = useMemo(
		() =>
			new QueryClient({
				mutationCache: new MutationCache({
					onError: (error) => {
						const errorWithType = error as ErrorWithType;
						if (errorWithType.type === READING_MODE_ERROR) {
							dispatchers.dispatchReadingModeError()(dispatch);
						}
					},
				}),
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
						refetchOnMount: false,
					},
				},
			}),
		[],
	);
	window.__TANSTACK_QUERY_CLIENT__ = queryClient;

	return mbc.isNative() ? (
		<PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
			{children}
		</PersistQueryClientProvider>
	) : (
		<OriginalQueryClientProvider client={queryClient}>{children}</OriginalQueryClientProvider>
	);
};
export default QueryClientProvider;
